import React from 'react';
import { Link, useRouteMatch } from 'react-router-dom';

import { locale } from '@trqk/utils';

import { FULL_PATHS, PATHS, CHILD_PATHS, HASHES } from '../../../constants';

export const SomeStatementsFailed = ({
    remove,
    input,
}: ToastRenderContentProps<{
    errorTasks: Task<Record<string, unknown>, { qrrOverage?: number }>[];
    successTasks: UnknownTask[];
}>): JSX.Element => {
    const errorTasks: Task<Record<string, unknown>, { qrrOverage?: number }>[] =
        input?.errorTasks ?? [];
    const successTasks: UnknownTask[] = input?.successTasks ?? [];

    let errorWarning: Maybe<string> = null;

    if (errorTasks.length > 0) {
        errorWarning = `${locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.THERE_WAS_A_PROBLEM_WITH} ${errorTasks.length} ${locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.OF_YOUR_STATEMENTS}.`;
    }

    let successInfo: Maybe<string> = null;

    if (successTasks.length === 1) {
        successInfo = `${successTasks.length} ${locale.enUS.INGESTION.TOASTS.STATEMENT_SUCCESS.OF_YOUR_STATEMENTS_WAS} ${locale.enUS.INGESTION.TOASTS.STATEMENT_SUCCESS.INGESTED_SUCCESSFULLY}`;
    }

    if (successTasks.length > 1) {
        successInfo = `${successTasks.length} ${locale.enUS.INGESTION.TOASTS.STATEMENT_SUCCESS.OF_YOUR_STATEMENTS_WERE} ${locale.enUS.INGESTION.TOASTS.STATEMENT_SUCCESS.INGESTED_SUCCESSFULLY}`;
    }

    const match = useRouteMatch({
        path: `${PATHS.STATEMENTS}${CHILD_PATHS.STATEMENTS.MANAGE}`,
        strict: true,
        sensitive: true,
    });

    const containsQrrOverage = errorTasks.some(
        (
            task: Task<Record<string, unknown>, { qrrOverage?: number }>,
        ): boolean => task?.output?.qrrOverage !== undefined,
    );

    const showAlertsTabLink =
        errorTasks.length > 0 && match === null && !containsQrrOverage;

    const showPaymentPlanLink = errorTasks.length > 0 && containsQrrOverage;

    return (
        <div style={{ width: 410 }} className="px-2">
            <div className="font-bold text-highlight-orange tracking-wider mb-2">
                {locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.INGESTION_ISSUE}
            </div>
            {errorWarning && (
                <div className="mb-2 font-bold">{errorWarning}</div>
            )}
            {successInfo && <div className="mb-2">{successInfo}</div>}
            {showAlertsTabLink && (
                <div className="font-normal text-tertiary-500">
                    {
                        locale.enUS.INGESTION.TOASTS.SOME_STATEMENTS_FAILED
                            .YOU_CAN_SEE_RESULTS
                    }
                    &nbsp;
                    <Link
                        to={({ search }) => ({
                            hash: HASHES.MANAGE_STATEMENTS.ALERTS,
                            pathname: FULL_PATHS.MANAGE_STATEMENTS_CHARTS,
                            search,
                        })}
                    >
                        <button
                            className="text-primary-500 underline"
                            type="button"
                            onClick={(): void => {
                                remove({
                                    canceled: false,
                                    success: true,
                                });
                            }}
                        >
                            {
                                locale.enUS.INGESTION.TOASTS
                                    .SOME_STATEMENTS_FAILED.ALERTS_TAB
                            }
                        </button>
                    </Link>
                    &nbsp;
                    {
                        locale.enUS.INGESTION.TOASTS.SOME_STATEMENTS_FAILED
                            .OF_THE_STATEMENT_MANAGER
                    }
                </div>
            )}
            {showPaymentPlanLink && (
                <div className="font-normal text-tertiary-500">
                    {
                        locale.enUS.INGESTION.TOASTS.SOME_STATEMENTS_FAILED
                            .ONE_OR_MORE_STATEMENTS_EXCEEDED_CAPACITY
                    }{' '}
                    <Link
                        to={({ search }) => ({
                            hash: HASHES.SETTINGS.PAYMENT_PLAN,
                            pathname: PATHS.SETTINGS,
                            search,
                            state: {
                                fromLink: true,
                            },
                        })}
                    >
                        <button
                            className="text-primary-500 underline"
                            type="button"
                            onClick={(): void => {
                                remove({
                                    canceled: false,
                                    success: true,
                                });
                            }}
                        >
                            {
                                locale.enUS.INGESTION.TOASTS
                                    .SOME_STATEMENTS_FAILED.UPDATE_YOUR_PLAN
                            }
                        </button>
                    </Link>{' '}
                    {
                        locale.enUS.INGESTION.TOASTS.SOME_STATEMENTS_FAILED
                            .TO_CONTINUE_UPLOADING
                    }
                </div>
            )}
        </div>
    );
};
